import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { canAccessPath } from '@/lib/role-access';
import {
  QueryFailureNotice,
  LocalizedApiError,
  resolveIssueNavigation,
  type IssueNavigationIntent,
} from '@/features/audit-issues';

const NS = 'approvals';
const KEY = 'approvals' as const;
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

interface ApprovalListItem {
  audit_approval_id: string;
  target_record_type: string;
  target_record_id: string;
  approval_type: string;
  requested_by: string;
  requested_by_name: string | null;
  requested_at: string;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  status: string;
  reason: string;
  decision_note: string | null;
  batch_year_month: string | null;
  batch_facility_name?: string | null;
  /** Business identity of the target (exception code / master / destination). */
  target_display?: string | null;
  /** Monthly batch the target belongs to (navigation context). */
  target_batch_id?: string | null;
  /** For `package` targets: the owning inbound lot (deep navigation). */
  target_lot_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Backend `target_record_type` vocabulary (spec-13 §8.8): master, exception,
 * batch, destination, package. Maps each to its localized record-type key —
 * no raw vocabulary string may render.
 */
const TARGET_TYPE_RECORD_KEY: Record<string, string> = {
  batch: 'monthly_batch',
  package: 'inbound_package',
  exception: 'exception',
  destination: 'destination',
  master: 'master',
};

/** Types whose backend row is expected to carry a `target_display` value. */
const EXPECTS_TARGET_DISPLAY = new Set(['exception', 'master', 'destination', 'package']);

const targetTypeLabel = (
  t: (key: string, opts?: Record<string, unknown>) => string,
  type: string,
): string => {
  const recordKey = TARGET_TYPE_RECORD_KEY[type];
  return recordKey
    ? t(`efs.issues.record_types.${recordKey}`, { ns: 'issues' })
    : t('efs.issues.target.record_generic', { ns: 'issues' });
};

/**
 * Allowlisted navigation intent for one approval row's target (spec-13
 * §8.8/§6.2). `master` targets are ambiguous (could resolve to several
 * master lists) so they render no navigation.
 */
const navigationIntentFor = (row: ApprovalListItem): IssueNavigationIntent | null => {
  switch (row.target_record_type) {
    case 'batch':
      return { kind: 'monthly_batch', params: { batch_id: row.target_record_id } };
    case 'package':
      if (row.target_batch_id && row.target_lot_id) {
        return {
          kind: 'batch_inbound_lot',
          params: { batch_id: row.target_batch_id, lot_id: row.target_lot_id },
        };
      }
      return null;
    case 'exception':
      if (row.target_batch_id) {
        return {
          kind: 'batch_exceptions',
          params: { batch_id: row.target_batch_id, exception_id: row.target_record_id },
        };
      }
      return null;
    case 'destination':
      return { kind: 'master_destination', params: { q: row.target_display ?? undefined } };
    case 'master':
    default:
      return null;
  }
};

interface ApprovalListMeta {
  total: number;
  page: number;
  per_page: number;
}

type StatusFilter = 'pending_approval' | 'all';

const fetchApprovals = async (params: {
  status: StatusFilter;
  page: number;
  per_page: number;
}): Promise<{ data: ApprovalListItem[]; meta: ApprovalListMeta }> => {
  const query: Record<string, unknown> = { page: params.page, per_page: params.per_page };
  if (params.status !== 'all') query.status = params.status;
  const res = await apiClient.get<ApiSuccess<ApprovalListItem[], ApprovalListMeta>>('/approvals', {
    params: query,
  });
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page,
    per_page: params.per_page,
  };
  return { data, meta };
};

interface ApproveInput {
  id: string;
  decision_note: string;
  action: 'approve' | 'reject';
}

const useApproveAction = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, ApproveInput>({
    mutationFn: async ({ id, decision_note, action }) => {
      await apiClient.post(`/approvals/${id}/${action}`, { decision_note });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['monthly-batches'] });
    },
  });
};

const ApprovalActionDialog = ({
  approval,
  action,
  open,
  onOpenChange,
  approve,
}: {
  approval: ApprovalListItem;
  action: 'approve' | 'reject';
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Shared page-level mutation so pending state is attributable to one row. */
  approve: UseMutationResult<void, ApiClientError, ApproveInput>;
}) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const [note, setNote] = useState('');
  const canViewTechnicalDetails = hasPermission(user, 'system.admin');
  const targetLabel = targetTypeLabel(t, approval.target_record_type);
  const unavailable = t('efs.issues.target.unavailable', { ns: 'issues' });
  const targetDisplay = approval.target_display ?? approval.batch_year_month ?? unavailable;
  const targetContext = [approval.batch_year_month, approval.batch_facility_name]
    .filter((value): value is string => Boolean(value))
    .join(' / ');
  const requester =
    approval.requested_by_name ?? t('efs.issues.target.code_unavailable', { ns: 'issues' });
  const approvalType = t(`efs.approvals.types.${approval.approval_type}`, {
    defaultValue: approval.approval_type,
  });
  const status = t(`efs.approvals.status.${approval.status}`, { defaultValue: approval.status });

  const onSubmit = async () => {
    await approve.mutateAsync({ id: approval.audit_approval_id, decision_note: note, action });
    setNote('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action === 'approve'
              ? t('efs.approvals.dialog.approve_title', { defaultValue: '承認' })
              : t('efs.approvals.dialog.reject_title', { defaultValue: '却下' })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.approvals.dialog.note_description', {
              defaultValue: '決定理由を入力してください（任意）',
            })}
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-xs">
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.target_record_type', { defaultValue: '対象種別' })}
          </dt>
          <dd className="text-efs-text-primary">{targetLabel}</dd>
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.target', { defaultValue: '対象' })}
          </dt>
          <dd className="text-efs-text-primary">{targetDisplay}</dd>
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.target_context', { defaultValue: '対象月・拠点' })}
          </dt>
          <dd className="text-efs-text-primary">{targetContext || unavailable}</dd>
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.requested_by', { defaultValue: '申請者' })}
          </dt>
          <dd className="text-efs-text-primary">{requester}</dd>
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.status', { defaultValue: '状態' })}
          </dt>
          <dd className="text-efs-text-primary">{status}</dd>
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.approval_type', { defaultValue: '種別' })}
          </dt>
          <dd className="text-efs-text-primary">{approvalType}</dd>
          <dt className="font-medium text-efs-text-muted">
            {t('efs.approvals.fields.reason', { defaultValue: '申請理由' })}
          </dt>
          <dd className="text-efs-text-primary">{approval.reason}</dd>
        </dl>

        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('efs.approvals.dialog.note_placeholder', { defaultValue: '決定理由...' })}
        />
        {approve.isError ? (
          <LocalizedApiError error={approve.error} showTechnicalDetails={canViewTechnicalDetails} />
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={approve.isPending}
          >
            {t('efs.common.actions.cancel', { defaultValue: 'キャンセル' })}
          </Button>
          <Button
            variant={action === 'approve' ? 'default' : 'destructive'}
            onClick={onSubmit}
            disabled={approve.isPending}
          >
            {approve.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {action === 'approve'
              ? t('efs.approvals.actions.approve', { defaultValue: '承認する' })
              : t('efs.approvals.actions.reject', { defaultValue: '却下する' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const statusBadgeVariant = (
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'approved':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'pending_approval':
      return 'secondary';
    default:
      return 'outline';
  }
};

export const ApprovalsPage = () => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const navigate = useNavigate();
  const canDecide = hasPermission(user, 'approval.decide');
  const approve = useApproveAction();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending_approval');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [dialogState, setDialogState] = useState<{
    approval: ApprovalListItem;
    action: 'approve' | 'reject';
  } | null>(null);

  const query = useQuery<{ data: ApprovalListItem[]; meta: ApprovalListMeta }, ApiClientError>({
    queryKey: [KEY, 'list', { status: statusFilter, page, per_page: perPage }],
    queryFn: () => fetchApprovals({ status: statusFilter, page, per_page: perPage }),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  const setFilter = (next: StatusFilter) => {
    setStatusFilter(next);
    setPage(1);
  };

  const columns = useMemo<MasterTableColumn<ApprovalListItem>[]>(
    () => [
      {
        key: 'approval_type',
        header: t('efs.approvals.fields.approval_type', { defaultValue: '種別' }),
        cell: (row) => (
          <Badge variant="outline">
            {t(`efs.approvals.types.${row.approval_type}`, { defaultValue: row.approval_type })}
          </Badge>
        ),
      },
      {
        key: 'target',
        header: t('efs.approvals.fields.target', { defaultValue: '対象' }),
        cell: (row) => {
          const typeLabel = targetTypeLabel(t, row.target_record_type);
          const intent = navigationIntentFor(row);
          const resolved = intent ? resolveIssueNavigation(intent) : null;
          const canNavigate = !!resolved && resolved.ok && canAccessPath(user, resolved.path);
          const showUnavailable =
            !canNavigate &&
            !row.target_display &&
            EXPECTS_TARGET_DISPLAY.has(row.target_record_type);
          const navLabel = t('efs.approvals.actions.open_target', {
            type: typeLabel,
            defaultValue: `${typeLabel}を開く`,
          });
          return (
            <div className="min-w-0 space-y-1">
              <div className="text-sm text-efs-text-primary">{typeLabel}</div>
              {row.target_display ? (
                <div className="text-xs font-medium text-efs-text-primary">
                  {row.target_display}
                </div>
              ) : null}
              {row.batch_year_month ? (
                <div className="text-xs text-efs-text-muted">{row.batch_year_month}</div>
              ) : null}
              {canNavigate ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-auto px-2 py-1 text-xs"
                  aria-label={navLabel}
                  onClick={() => navigate((resolved as { ok: true; path: string }).path)}
                >
                  {navLabel}
                </Button>
              ) : showUnavailable ? (
                <div className="text-xs text-efs-text-muted">
                  {t('efs.issues.target.unavailable', { ns: 'issues' })}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'reason',
        header: t('efs.approvals.fields.reason', { defaultValue: '申請理由' }),
        cell: (row) => <span className="text-sm">{row.reason}</span>,
      },
      {
        key: 'requested_by',
        header: t('efs.approvals.fields.requested_by', { defaultValue: '申請者' }),
        cell: (row) =>
          row.requested_by_name ?? t('efs.issues.target.code_unavailable', { ns: 'issues' }),
      },
      {
        key: 'requested_at',
        header: t('efs.approvals.fields.requested_at', { defaultValue: '申請日時' }),
        cell: (row) => (
          <span className="text-xs">{new Date(row.requested_at).toLocaleString()}</span>
        ),
      },
      {
        key: 'status',
        header: t('efs.approvals.fields.status', { defaultValue: '状態' }),
        cell: (row) => (
          <Badge variant={statusBadgeVariant(row.status)}>
            {t(`efs.approvals.status.${row.status}`, { defaultValue: row.status })}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => {
          if (!canDecide || row.status !== 'pending_approval') return null;
          // Row-specific mutation pending: a hanging approve/reject only
          // disables the row it belongs to (spec-13 §9 mutation-pending).
          const isRowPending = approve.isPending && approve.variables?.id === row.audit_approval_id;
          return (
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={isRowPending}
                onClick={() => setDialogState({ approval: row, action: 'approve' })}
              >
                {t('efs.approvals.actions.approve', { defaultValue: '承認' })}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={isRowPending}
                onClick={() => setDialogState({ approval: row, action: 'reject' })}
              >
                {t('efs.approvals.actions.reject', { defaultValue: '却下' })}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, canDecide, user, navigate, approve.isPending, approve.variables],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.common.nav.approvals', { defaultValue: '承認' })}
        subtitle={t('efs.approvals.subtitle', { defaultValue: '稽核包・結算承認の管理' })}
        count={total}
      />

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={statusFilter === 'pending_approval' ? 'default' : 'outline'}
          onClick={() => setFilter('pending_approval')}
        >
          {t('efs.approvals.filters.pending', { defaultValue: '未承認' })}
        </Button>
        <Button
          size="sm"
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
        >
          {t('efs.approvals.filters.all', { defaultValue: 'すべて' })}
        </Button>
      </div>

      {query.isError ? (
        <QueryFailureNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <MasterTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.audit_approval_id}
          isLoading={query.isLoading}
          emptyMessage={t('efs.approvals.empty', { defaultValue: '承認待ちの項目はありません' })}
          pagination={{
            page,
            perPage,
            total,
            onPageChange: setPage,
            onPerPageChange: (nextPerPage) => {
              setPage(1);
              setPerPage(nextPerPage);
            },
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
      )}

      {dialogState ? (
        <ApprovalActionDialog
          approval={dialogState.approval}
          action={dialogState.action}
          open={!!dialogState}
          approve={approve}
          onOpenChange={(v) => {
            if (!v) setDialogState(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default ApprovalsPage;
